import React, { useEffect, useState } from 'react'

import { SxProps } from '@mui/material'

import {
  ParsedDashboardFilterOptions,
  TPutResourceFilter
} from 'types/GlobalDashboardFilter'

import { useReport } from 'redux/hooks/Reports'
import { useReportActions } from 'redux/actions/Reports'
import { useResourceFilterActions } from 'redux/actions/ResourceFilter'
import {
  useResourceFilterData,
  useResourceFilterOptions,
  useResourceFilterStatus
} from 'redux/hooks/ResourceFilter'
import { useTranslation } from 'language/translator'

import { formatResourceFilterOptions } from 'utils/functions'

import Button from 'components_new/atoms/Button'
import Grid2 from '@mui/material/Unstable_Grid2'
import Skeleton from 'components_new/atoms/Skeleton'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

import DndBlocks from 'components_new/molecules/DndBlocks'
import StandardFilter from 'components_new/molecules/StandardFilter'
import EditUserFiltersDialog from 'components_new/organisms/DashboardUserFilter/dialogs/EditUserFilterDialog'

interface ReportFiltersProps {
  editMode?: boolean
  activeFilters: Record<string, string[]>
  id: string
  setActiveFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >
  sx?: SxProps
}

const ReportFilters = (props: ReportFiltersProps) => {
  const { activeFilters, editMode, id, setActiveFilters, sx } = props

  const report = useReport(id)
  const resourceFilterStatus = useResourceFilterStatus()
  const resourceFilterOptions = useResourceFilterOptions()
  const resourceFilterData = useResourceFilterData()

  const { t } = useTranslation()

  const { tryPutReportFilters } = useReportActions()
  const { tryGetResourceFilterOptions, tryGetResourceFilterData } =
    useResourceFilterActions()

  useEffect(() => {
    if (!resourceFilterStatus.fetched && !resourceFilterStatus.fetching) {
      tryGetResourceFilterOptions()
    }
  }, [])

  const [filterOrder, setFilterOrder] = useState<string[]>([])
  const [dialog, setDialog] = useState<boolean>(false)

  useEffect(() => {
    if (report) {
      // set state for filter order
      setFilterOrder(report.report_filters.map((item) => item.attribute_id))

      // fetch filter options
      const fetchOptions = report.report_filters
        .map((dashboardFilter) => ({
          saveKey: dashboardFilter.attribute_id,
          attributeId: dashboardFilter.attribute_id
        }))
        .filter((item) => !(item.saveKey in resourceFilterData))

      if (fetchOptions.length > 0) {
        tryGetResourceFilterData(fetchOptions)
      }
    }
  }, [report?.report_filters])

  const handleReorderFilters = (newOrder: string[]) => {
    if (!report) return

    setFilterOrder(newOrder)

    const payload = newOrder
      .map((attributeId, index) => {
        const filter = report.report_filters.find(
          (f) => f.attribute_id === attributeId
        )

        if (!filter) return null

        return {
          attribute_id: filter.attribute_id,
          index
        }
      })
      .filter((item): item is TPutResourceFilter => !!item)

    tryPutReportFilters(id, payload)
  }

  const filterOptionsMap = formatResourceFilterOptions(
    report?.report_filters || [],
    resourceFilterOptions,
    resourceFilterData,
    null
  )

  const filterItems = filterOrder
    .map((attributeId) => filterOptionsMap.get(attributeId))
    .filter((item): item is ParsedDashboardFilterOptions => !!item)
    .map((item) => {
      const options = (item.options ?? [])
        .filter(Boolean)
        .map((value) => ({ id: value, title: value }))

      return {
        content: (
          <StandardFilter
            disabled={editMode}
            fullWidth={true}
            key={item.attribute_id}
            loading={item.options === undefined}
            onChange={(values) =>
              setActiveFilters((prevState) => ({
                ...prevState,
                [item.attribute_id]: values
              }))
            }
            options={options}
            selected={activeFilters[item.attribute_id] || []}
            size="medium"
            sx={{ height: 40 }}
            title={item.title}
          />
        ),
        id: item.attribute_id
      }
    })

  // loading
  if (!report)
    return (
      <Grid2
        container={true}
        spacing={1}
        sx={{
          ...sx
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Grid2 key={i} xs={3}>
            <Skeleton height={40} variant="rounded" />
          </Grid2>
        ))}
      </Grid2>
    )

  // empty 1
  if (filterItems.length === 0 && !editMode) return null

  return (
    <>
      {
        // empty 2
        filterItems.length === 0 ? (
          <EmptyPlaceholder
            actions={
              editMode ? (
                <Button onClick={() => setDialog(true)}>
                  {t('reports', 'empty_filters_cta')}
                </Button>
              ) : undefined
            }
            background={true}
            body={t('reports', 'empty_filters_body')}
            height={172}
            size="small"
            sx={sx}
            title={t('reports', 'empty_filters_title')}
          />
        ) : (
          // success
          <DndBlocks
            addBlock={{
              onClick: () => setDialog(true)
            }}
            editable={Boolean(editMode)}
            handleDragEnd={handleReorderFilters}
            items={filterItems}
            orientation="grid"
            sx={sx}
          />
        )
      }

      {/*-- dialogs --*/}
      <EditUserFiltersDialog
        availableDashboardFilterOptions={resourceFilterOptions}
        dashboardFilterOptions={filterOptionsMap}
        onClose={() => setDialog(false)}
        onSave={(filters) => {
          setFilterOrder(filters.map((f) => f.attribute_id))
          tryPutReportFilters(id, filters)
        }}
        open={dialog}
      />
    </>
  )
}

export default ReportFilters
